import React, { useState, useEffect, useMemo } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, ShieldOff, Link2, Plus, Save, Trash2, Pencil, X, ExternalLink, Search, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import ConfirmDialog from '@/components/admin/ConfirmDialog';

const TYPE_LABELS = {
  product: { label: 'PRODUCT PAGE', cls: 'border-primary/30 bg-primary/10 text-primary' },
  page: { label: 'INTERNAL PAGE', cls: 'border-white/10 bg-white/5 text-white/60' },
  url: { label: 'EXTERNAL URL', cls: 'border-blue-500/30 bg-blue-500/10 text-blue-400' },
};

const EMPTY = { source_path: '', destination: '', destination_type: 'page', product_id: '', notes: '', active: true };

export default function AdminRedirects() {
  const { user: currentUser, isLoadingAuth: authLoading } = useAuth();
  const { toast } = useToast();
  const [modal, setModal] = useState(null); // null | { mode: 'create'|'edit', data }
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [productSearch, setProductSearch] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [query, setQuery] = useState('');
  const qc = useQueryClient();

  const { data: redirects = [], isLoading } = useQuery({
    queryKey: ['url-redirects'],
    queryFn: () => base44.entities.UrlRedirect.list('-updated_date', 500),
    enabled: currentUser?.role === 'admin',
  });

  const { data: products = [] } = useQuery({
    queryKey: ['redirect-products', productSearch],
    queryFn: () => base44.entities.Product.list('-updated_date', 200),
    enabled: currentUser?.role === 'admin' && form.destination_type === 'product',
  });

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return redirects;
    return redirects.filter((r) =>
      `${r.source_path} ${r.destination} ${r.notes || ''}`.toLowerCase().includes(q)
    );
  }, [redirects, query]);

  const filteredProducts = useMemo(() => {
    const q = productSearch.toLowerCase().trim();
    const list = (products || []).filter((p) => p.published !== false);
    if (!q) return list.slice(0, 80);
    return list.filter((p) => `${p.name} ${p.sku || ''} ${p.customer_facing_name || ''}`.toLowerCase().includes(q)).slice(0, 80);
  }, [products, productSearch]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="text-center">
          <ShieldOff className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p>
          <Link to="/admin" className="inline-block mt-5 text-primary text-xs font-bold tracking-widest hover:underline">SIGN IN →</Link>
        </div>
      </div>
    );
  }

  const openCreate = () => { setForm(EMPTY); setErr(null); setProductSearch(''); setModal({ mode: 'create' }); };
  const openEdit = (r) => {
    setForm({
      source_path: r.source_path || '',
      destination: r.destination || '',
      destination_type: r.destination_type || 'page',
      product_id: r.product_id || '',
      notes: r.notes || '',
      active: r.active !== false,
    });
    setErr(null);
    setProductSearch('');
    setModal({ mode: 'edit', id: r.id });
  };

  const onTypeChange = (type) => {
    setForm((f) => {
      if (type === 'product') {
        const pid = f.product_id || '';
        return { ...f, destination_type: 'product', destination: pid ? `/shop/${pid}` : '' };
      }
      if (type === 'page') {
        return { ...f, destination_type: 'page', product_id: '', destination: f.destination_type === 'url' ? '' : f.destination };
      }
      // url
      return { ...f, destination_type: 'url', product_id: '', destination: f.destination && f.destination.startsWith('/') ? '' : f.destination };
    });
  };

  const onPickProduct = (p) => {
    setForm((f) => ({ ...f, product_id: p.id, destination: `/shop/${p.id}`, destination_type: 'product' }));
  };

  const save = async () => {
    setErr(null);
    let source = form.source_path.trim().toLowerCase();
    if (!source.startsWith('/')) source = '/' + source;
    if (!source || source === '/') { setErr('Enter an old URL path (e.g. /simpowbrk).'); return; }
    let dest = form.destination.trim();
    if (!dest) { setErr('Enter a destination.'); return; }
    if (form.destination_type !== 'url' && !dest.startsWith('/')) dest = '/' + dest;

    setSaving(true);
    try {
      const payload = {
        source_path: source,
        destination: dest,
        destination_type: form.destination_type,
        product_id: form.destination_type === 'product' ? form.product_id : '',
        notes: form.notes.trim(),
        active: form.active,
      };
      if (modal.mode === 'create') {
        await base44.entities.UrlRedirect.create(payload);
      } else {
        await base44.entities.UrlRedirect.update(modal.id, payload);
      }
      qc.invalidateQueries({ queryKey: ['url-redirects'] });
      setModal(null);
    } catch (e) {
      setErr(e.message || 'Save failed');
    }
    setSaving(false);
  };

  const toggleActive = async (r) => {
    try {
      await base44.entities.UrlRedirect.update(r.id, { active: !r.active });
      qc.invalidateQueries({ queryKey: ['url-redirects'] });
    } catch (e) {
      toast({ title: 'Update failed', description: e.message, variant: 'destructive' });
    }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    setDeletingId(deleteTarget.id);
    try {
      await base44.entities.UrlRedirect.delete(deleteTarget.id);
      qc.invalidateQueries({ queryKey: ['url-redirects'] });
      setDeleteTarget(null);
    } catch (e) {
      toast({ title: 'Delete failed', description: e.message, variant: 'destructive' });
    }
    setDeletingId(null);
  };

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      <header className="py-12 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-6xl mx-auto">
          <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-white/30 hover:text-white text-xs font-bold tracking-widest mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
          </Link>
          <p className="text-primary text-xs font-bold tracking-widest mb-2">SYSTEM &amp; INTEGRATIONS</p>
          <h1 className="text-white font-black text-3xl md:text-4xl uppercase">MANAGE REDIRECTS</h1>
          <p className="text-white/30 text-sm mt-3 max-w-2xl">
            Point old links (Google-indexed store URLs, short slugs) straight to a product page or any destination. The site checks these before falling back to automatic product-slug matching.
          </p>
        </div>
      </header>

      <section className="px-6 lg:px-10 py-8 max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row gap-3 items-center justify-between mb-6">
          <div className="relative w-full sm:max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search redirects…"
              className="w-full bg-[#111115] border border-white/10 focus:border-primary text-white text-sm pl-9 pr-3 py-2.5 outline-none"
            />
          </div>
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-2 bg-primary/20 border border-primary/40 hover:bg-primary hover:border-primary text-white font-bold text-xs tracking-widest px-5 py-2.5 transition-colors w-full sm:w-auto justify-center"
          >
            <Plus className="w-4 h-4" /> ADD REDIRECT
          </button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 border border-white/5 bg-[#111115]">
            <Link2 className="w-10 h-10 text-white/10 mx-auto mb-4" />
            <p className="text-white/30 text-sm">{redirects.length === 0 ? 'No redirects yet.' : 'No matches.'}</p>
          </div>
        ) : (
          <div className="border border-white/5 bg-[#111115] divide-y divide-white/5">
            {filtered.map((r) => {
              const t = TYPE_LABELS[r.destination_type] || TYPE_LABELS.page;
              return (
                <div key={r.id} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 md:gap-4 items-center px-4 py-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <code className="text-white font-mono text-sm break-all">{r.source_path}</code>
                      {!r.active && <span className="text-[10px] font-bold tracking-widest text-white/30 border border-white/10 px-1.5 py-0.5">INACTIVE</span>}
                    </div>
                    {r.notes && <p className="text-white/30 text-xs mt-1 truncate">{r.notes}</p>}
                  </div>
                  <div className="min-w-0 flex items-center gap-2">
                    <span className={`text-[10px] font-bold tracking-widest border px-1.5 py-0.5 shrink-0 ${t.cls}`}>{t.label}</span>
                    <code className="text-white/60 font-mono text-xs break-all truncate">{r.destination}</code>
                    {r.destination_type === 'url' && <ExternalLink className="w-3 h-3 text-white/30 shrink-0" />}
                  </div>
                  <div className="flex items-center justify-end gap-1.5">
                    <button onClick={() => toggleActive(r)} className="text-[10px] font-bold tracking-widest text-white/50 hover:text-white border border-white/10 hover:border-white/30 px-2.5 py-1.5 transition-colors">
                      {r.active ? 'PAUSE' : 'RESUME'}
                    </button>
                    <button onClick={() => openEdit(r)} className="text-white/40 hover:text-primary p-1.5 transition-colors" title="Edit">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => setDeleteTarget(r)} disabled={deletingId === r.id} className="text-white/40 hover:text-red-400 p-1.5 transition-colors disabled:opacity-40" title="Delete">
                      {deletingId === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {modal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div className="bg-[#111115] border border-white/10 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 sticky top-0 bg-[#111115]">
              <p className="text-white font-bold text-sm tracking-widest">{modal.mode === 'create' ? 'NEW REDIRECT' : 'EDIT REDIRECT'}</p>
              <button onClick={() => setModal(null)} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            <div className="p-5 space-y-5">
              <div>
                <label className="block text-white/40 text-[10px] font-bold tracking-widest mb-2">OLD URL PATH (FROM)</label>
                <input
                  value={form.source_path}
                  onChange={(e) => setForm({ ...form, source_path: e.target.value })}
                  placeholder="/simpowbrk"
                  className="w-full bg-[#0d0d0f] border border-white/10 focus:border-primary text-white text-sm font-mono px-4 py-3 outline-none"
                />
                <p className="text-white/30 text-[10px] mt-1">Must start with "/". Case-insensitive.</p>
              </div>

              <div>
                <label className="block text-white/40 text-[10px] font-bold tracking-widest mb-2">DESTINATION TYPE</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { v: 'product', l: 'PRODUCT' },
                    { v: 'page', l: 'PAGE' },
                    { v: 'url', l: 'EXTERNAL URL' },
                  ].map((o) => (
                    <button
                      key={o.v}
                      onClick={() => onTypeChange(o.v)}
                      className={`text-[10px] font-bold tracking-widest px-2 py-2.5 transition-colors ${
                        form.destination_type === o.v ? 'bg-primary border border-primary text-white' : 'border border-white/10 text-white/50 hover:text-white'
                      }`}
                    >
                      {o.l}
                    </button>
                  ))}
                </div>
              </div>

              {form.destination_type === 'product' && (
                <div>
                  <label className="block text-white/40 text-[10px] font-bold tracking-widest mb-2">SELECT PRODUCT</label>
                  <div className="relative mb-2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" />
                    <input
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      placeholder="Search products by name or SKU…"
                      className="w-full bg-[#0d0d0f] border border-white/10 focus:border-primary text-white text-sm pl-9 pr-3 py-2.5 outline-none"
                    />
                  </div>
                  <div className="border border-white/10 bg-[#0d0d0f] max-h-56 overflow-y-auto divide-y divide-white/5">
                    {filteredProducts.length === 0 ? (
                      <p className="text-white/30 text-xs p-4 text-center">No products found.</p>
                    ) : filteredProducts.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => onPickProduct(p)}
                        className={`w-full text-left px-3 py-2.5 flex items-center gap-3 transition-colors ${
                          form.product_id === p.id ? 'bg-primary/10 border-l-2 border-primary' : 'hover:bg-white/5 border-l-2 border-transparent'
                        }`}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-white text-xs font-medium truncate">{p.name}</p>
                          <p className="text-white/30 text-[10px] font-mono">{p.sku || 'no sku'}</p>
                        </div>
                        {form.product_id === p.id && <span className="text-primary text-[10px] font-bold tracking-widest">SELECTED</span>}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-white/40 text-[10px] font-bold tracking-widest mb-2">
                  DESTINATION {form.destination_type === 'product' && '(auto-filled from product)'}
                </label>
                <input
                  value={form.destination}
                  onChange={(e) => setForm({ ...form, destination: e.target.value })}
                  placeholder={form.destination_type === 'url' ? 'https://example.com' : '/bookings'}
                  disabled={form.destination_type === 'product'}
                  className="w-full bg-[#0d0d0f] border border-white/10 focus:border-primary text-white text-sm font-mono px-4 py-3 outline-none disabled:opacity-50"
                />
              </div>

              <div>
                <label className="block text-white/40 text-[10px] font-bold tracking-widest mb-2">NOTES (optional)</label>
                <input
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="e.g. old Google-indexed link"
                  className="w-full bg-[#0d0d0f] border border-white/10 focus:border-primary text-white text-sm px-4 py-3 outline-none"
                />
              </div>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  className="w-4 h-4 accent-[#c0392b]"
                />
                <span className="text-white/60 text-xs">Active (redirect is live)</span>
              </label>

              {err && <p className="text-red-400 text-xs">{err}</p>}
            </div>

            <div className="px-5 py-4 border-t border-white/10 flex items-center justify-end gap-2 sticky bottom-0 bg-[#111115]">
              <button onClick={() => setModal(null)} className="text-white/50 hover:text-white text-xs font-bold tracking-widest px-4 py-2.5">CANCEL</button>
              <button
                onClick={save}
                disabled={saving}
                className="inline-flex items-center gap-2 bg-primary/20 border border-primary/40 hover:bg-primary hover:border-primary text-white font-bold text-xs tracking-widest px-5 py-2.5 transition-colors disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} SAVE
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete redirect"
        description={deleteTarget ? `Delete ${deleteTarget.source_path} → ${deleteTarget.destination}? This cannot be undone.` : ''}
        confirmLabel="Delete"
        onConfirm={remove}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}